// Formatting shared by the weather safety components. Times are shown in UK
// local time explicitly, whatever the browser's zone: every forecast here is for
// a UK hill and every organiser reads it in UK time.
import axios from "axios";
import { API_BASE_URL } from "../config";
import { BAND_AMBER, BAND_RED, BAND_WATCH } from "./forecast";

export const SAFETY_API = `${API_BASE_URL}/safety`;

const TZ = "Europe/London";

export const fmtDateTime = (iso) =>
  iso ? new Date(iso).toLocaleString("en-GB", {
    timeZone: TZ, weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
  }) : "—";

export const fmtStepTime = (iso) =>
  iso ? new Date(iso).toLocaleString("en-GB", {
    timeZone: TZ, weekday: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
  }) : "—";

export const fmtWindow = (a) => {
  if (!a) return "";
  const end = new Date(a.window_end).toLocaleTimeString("en-GB", { timeZone: TZ, hour: "2-digit", minute: "2-digit" });
  return `${fmtDateTime(a.window_start)} – ${end}`;
};

// A point's exposure window: "Sat 10:07 – 13:14", or with the day on both ends
// when it runs overnight. Null when there's no window.
export const fmtPointWindow = (startIso, endIso) => {
  if (!startIso || !endIso) return null;
  const day = (iso) => new Date(iso).toLocaleDateString("en-GB", { timeZone: TZ });
  const time = (iso) => new Date(iso).toLocaleTimeString("en-GB", { timeZone: TZ, hour: "2-digit", minute: "2-digit" });
  const wd = (iso) => new Date(iso).toLocaleDateString("en-GB", { timeZone: TZ, weekday: "short" });
  return day(startIso) === day(endIso)
    ? `${wd(startIso)} ${time(startIso)} – ${time(endIso)}`
    : `${wd(startIso)} ${time(startIso)} – ${wd(endIso)} ${time(endIso)}`;
};

// <input type="datetime-local"> works in the browser's zone; convert to and from ISO.
export const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const off = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - off).toISOString().slice(0, 16);
};
export const fromLocalInput = (s) => (s ? new Date(s).toISOString() : null);

// Cell/chip colours for a band. Always paired with text (the band's name in the
// summary, bold type in the table), never colour alone. "Approaching amber" gets
// no cell colour — the tables stay about the thresholds — and an outlined chip.
export const BAND_BG = { [BAND_AMBER]: "#FFE3A6", [BAND_RED]: "#F7B2AE" };
export const BAND_CHIP = { [BAND_WATCH]: "warning", [BAND_AMBER]: "warning", [BAND_RED]: "error" };
export const BAND_CHIP_VARIANT = { [BAND_WATCH]: "outlined" };
export const BAND_LABEL = { [BAND_WATCH]: "Approaching amber", [BAND_AMBER]: "Amber", [BAND_RED]: "Red" };

// An assessment's overall severity (app/safety/evaluate.py): its worst point's
// band, or "benign" when every measure is short of approaching amber.
export const SEVERITY_LABEL = {
  benign: "Benign", [BAND_WATCH]: "Approaching amber", [BAND_AMBER]: "Amber", [BAND_RED]: "Red",
};
export const SEVERITY_CHIP = {
  benign: { color: "success", variant: "outlined" },
  [BAND_WATCH]: { color: "warning", variant: "outlined" },
  [BAND_AMBER]: { color: "warning", variant: "filled" },
  [BAND_RED]: { color: "error", variant: "filled" },
};
export const VERDICT_LABEL = { similar: "Much the same", better: "Better", worse: "Worse", mixed: "Mixed" };
export const VERDICT_SEVERITY = { similar: "info", better: "success", worse: "warning", mixed: "warning" };

// A number as the forecast tables show it.
export const fmtValue = (field, v) => {
  if (v === undefined || v === null) return "";
  if (field === "weather") return v;
  if (field === "visibility_m") return v >= 10000 ? "10k+" : Math.round(v);
  return Math.round(v * 10) / 10;
};

// "gust 47 mph" — a metric's value with its unit, from the config's hazards.
export const fmtMetric = (metric, v) =>
  `${fmtValue(metric.field, v)}${metric.unit ? (metric.unit.startsWith("°") ? metric.unit : ` ${metric.unit}`) : ""}`;

// Every metric in the config by field, with its hazard beside it.
export const metricsByField = (config) => {
  const out = {};
  for (const h of config?.hazards || []) for (const m of h.metrics) out[m.field] = { ...m, hazard: h };
  return out;
};

export const profileLabel = (config, key) =>
  config?.refresh_profiles?.find(p => p.key === key)?.label || key;

export const PROVIDER_LABEL = { metoffice: "Met Office", windy: "Windy", mwis: "MWIS" };

// The stored MWIS PDF needs the bearer token, so it can't be a plain link: fetch
// it and open an object URL (see components/DocumentLink.jsx for the pattern).
export async function openForecastPdf(forecastId) {
  const res = await axios.get(`${SAFETY_API}/forecasts/${forecastId}/pdf`, { responseType: "blob" });
  const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
  window.open(url, "_blank", "noopener");
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}
